import React, { useMemo, useCallback } from "react";
import { useCase } from "../../contexts/CaseContext";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { FolderIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import ErrorView from "../../components/ErrorView";
import LoadingView from "../../components/LoadingView";
import CaseTable from "./components/CaseTable";
import CaseGrid from "./components/CaseGrid";
import CaseFilters from "./components/CaseFilters";
import CreateCaseButton from "./components/CreateCaseButton";
import { ImportCaseFromPDFButton } from "./components/ImportCaseWizard";
import ViewToggle, { ViewMode } from "../../components/ViewToggle";
import { useListFilters } from "../../hooks/useListFilters";
import { ProgramType } from "@visitproof/types";

type FilterStatus = "all" | "open" | "closed";

interface CaseListFilters {
  searchTerm: string;
  statusFilter: FilterStatus;
  programTypeFilter: ProgramType | "all";
  viewMode: ViewMode;
}

const CASE_LIST_DEFAULTS: CaseListFilters = {
  searchTerm: "",
  statusFilter: "open", // Default to Open cases for power users
  programTypeFilter: "all",
  viewMode: "grid",
};

const CaseListPage: React.FC = () => {
  const { cases, loading, error } = useCase();

  // Persistent filters - remembers user's last filter selection
  const { filters, setFilter, resetFilters } = useListFilters({
    storageKey: "visitproof:caseListFilters",
    defaults: CASE_LIST_DEFAULTS,
  });

  const { searchTerm, statusFilter, programTypeFilter, viewMode } = filters;

  // Memoized filtered cases
  const filteredCases = useMemo(() => {
    return cases.filter((caseItem) => {
      // Status filter
      if (statusFilter !== "all" && caseItem.status !== statusFilter) {
        return false;
      }

      // Program type filter - check both programTypes array and legacy programType field
      if (programTypeFilter !== "all") {
        const hasProgramType =
          caseItem.programTypes?.includes(programTypeFilter) ||
          caseItem.programType === programTypeFilter;
        if (!hasProgramType) {
          return false;
        }
      }

      // Search filter
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const identifier = caseItem.identifier?.toLowerCase() || "";
        const notes = caseItem.notes?.toLowerCase() || "";

        // Search in identifier and notes
        if (!identifier.includes(searchLower) && !notes.includes(searchLower)) {
          return false;
        }
      }

      return true;
    });
  }, [cases, searchTerm, statusFilter, programTypeFilter]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter("searchTerm", e.target.value);
  }, [setFilter]);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter("statusFilter", e.target.value as FilterStatus);
  }, [setFilter]);

  const handleProgramTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter("programTypeFilter", e.target.value as ProgramType | "all");
  }, [setFilter]);

  const handleViewChange = useCallback((mode: ViewMode) => {
    setFilter("viewMode", mode);
  }, [setFilter]);

  const clearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  if (loading.list) {
    return <LoadingView />;
  }

  if (error.list) {
    return <ErrorView error={error.list} />;
  }

  // Check if filters differ from showing all data (not from defaults)
  const isFiltered = Boolean(searchTerm.trim() || statusFilter !== "all" || programTypeFilter !== "all");
  const hasCases = cases.length > 0;
  const hasFilteredCases = filteredCases.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <Heading>Cases</Heading>
          {hasCases && (
            <Text className="text-zinc-500 dark:text-zinc-400">
              {filteredCases.length} of {cases.length} cases
              {isFiltered && " (filtered)"}
            </Text>
          )}
        </div>
        <div className="flex items-center gap-3">
          {hasCases && (
            <ViewToggle view={viewMode} onViewChange={handleViewChange} />
          )}
          <ImportCaseFromPDFButton />
          <CreateCaseButton />
        </div>
      </div>

      {/* Filters */}
      {hasCases && (
        <CaseFilters
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          programTypeFilter={programTypeFilter}
          onSearchChange={handleSearchChange}
          onStatusChange={handleStatusChange}
          onProgramTypeChange={handleProgramTypeChange}
          onClearFilters={clearFilters}
          hasActiveFilters={isFiltered}
        />
      )}

      {/* Content */}
      {!hasCases ? (
        <EmptyState />
      ) : !hasFilteredCases ? (
        <NoResultsState searchTerm={searchTerm} onClearFilters={clearFilters} />
      ) : viewMode === "grid" ? (
        <CaseGrid cases={filteredCases} />
      ) : (
        <CaseTable cases={filteredCases} />
      )}
    </div>
  );
};

// Empty state when no cases exist
const EmptyState: React.FC = () => (
  <div className="mt-8 space-y-4 text-center lg:w-1/2 lg:mx-auto">
    <div className="flex justify-center">
      <div className="p-4 rounded-full bg-zinc-100 dark:bg-zinc-800">
        <FolderIcon className="w-8 h-8 text-zinc-600 dark:text-zinc-400" />
      </div>
    </div>
    <Heading>Let's Set Up Your First Case</Heading>
    <Text className="text-zinc-600 dark:text-zinc-400">
      Cases keep everything organized. Assign parents, supervisors, and
      children to a case so you can easily manage visitations, track
      events, and generate reports.
    </Text>
    <Subheading className="text-zinc-500 dark:text-zinc-500">
      Click the "Create Case" button to get started.
    </Subheading>
  </div>
);

// No results state when filters return no matches
const NoResultsState: React.FC<{ 
  searchTerm: string; 
  onClearFilters: () => void;
}> = ({ searchTerm, onClearFilters }) => (
  <div className="mt-8 space-y-4 text-center lg:w-1/2 lg:mx-auto">
    <div className="flex justify-center">
      <div className="p-4 rounded-full bg-zinc-100 dark:bg-zinc-800">
        <MagnifyingGlassIcon className="w-8 h-8 text-zinc-600 dark:text-zinc-400" />
      </div>
    </div>
    <Heading>No cases found</Heading>
    <Text className="text-zinc-600 dark:text-zinc-400">
      {searchTerm.trim() 
        ? `No cases match "${searchTerm}". Try adjusting your search terms.`
        : "No cases match your current filters."
      }
    </Text>
    <button
      onClick={onClearFilters}
      className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
    >
      Clear all filters
    </button>
  </div>
);

export default CaseListPage;
